import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useGroupedSizes } from "./hooks/useGroupedSizes";
import { GroupedSizesView } from "./GroupedSizesView";
import { SizeSchemaTab, SizeSchemaTabs } from "./Sizes.styled";
import { ResetFilters } from "src/organisms/ProductList/components/ResetFilters";
import { ScrollableRow } from "src/atoms/ScrollableRow";
import type { ListingFacet } from "src/types/adapters/listing.type";
import { useFacetContext } from "src/organisms/ProductList/components/Facets/facet.context";

type SizesListProps = {
  facet: ListingFacet;
};

function SizesList({ facet }: SizesListProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const groupRefs = useRef<Record<string, Element | null>>({});
  const [activeSchema, setActiveSchema] = useState<string | undefined>(
    undefined
  );
  const { refine, clearKey } = useFacetContext();

  const facetData = useMemo(
    () => (facet.type === "size" ? facet.data : []),
    [facet]
  );
  const { schemas, mappedSizes } = useGroupedSizes(facetData);

  const activeCount = useMemo(
    () => facetData.filter((option) => option.selected).length,
    [facetData]
  );

  useEffect(() => {
    if (schemas?.length && !activeSchema) {
      setActiveSchema(schemas[0]);
    }
  }, [schemas, activeSchema]);

  const handleScrollToSchema = (schema: string) => {
    if (!wrapperRef.current) return;
    const target = groupRefs.current[schema];
    if (!target) return;
    const wrapperRect = wrapperRef.current.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const top = targetRect.top - wrapperRect.top + wrapperRef.current.scrollTop;
    setActiveSchema(schema);
    wrapperRef.current.scrollTo({ top, behavior: "smooth" });
  };

  const handleRefine = useCallback(
    (value: string) => {
      refine(facet.key, value);
    },
    [facet.key, refine]
  );

  const handleResetFilters = useCallback(() => {
    clearKey(facet.key);
  }, [facet.key, clearKey]);

  return (
    <>
      {schemas?.length ? (
        <SizeSchemaTabs>
          <ScrollableRow gap="24px">
            {schemas?.map((key) => (
              <SizeSchemaTab
                key={key}
                className={key === activeSchema ? "active" : ""}
                onClick={() => handleScrollToSchema(key)}
              >
                {key}
              </SizeSchemaTab>
            ))}
          </ScrollableRow>
        </SizeSchemaTabs>
      ) : null}
      <div>
        <GroupedSizesView
          mappedSizes={mappedSizes}
          onSelect={handleRefine}
          wrapperRef={wrapperRef}
          groupRefCallback={(title, el) => (groupRefs.current[title] = el)}
        />
      </div>
      <ResetFilters activeCount={activeCount} onClear={handleResetFilters} />
    </>
  );
}

export { SizesList };
